import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { Database } from '@/types/supabase';
import { getSupabaseConfig } from '@/config/environment';

// Classe singleton para garantir uma única instância do Supabase
class SupabaseSingleton {
  private static instance: SupabaseSingleton;
  private client: SupabaseClient<Database> | null = null;
  private instanceId: string;
  private createdAt: number;

  private constructor() {
    this.instanceId = Math.random().toString(36).substring(2, 15);
    this.createdAt = Date.now();
    this.initializeClient();
  }

  public static getInstance(): SupabaseSingleton {
    if (!SupabaseSingleton.instance) {
      SupabaseSingleton.instance = new SupabaseSingleton();
    }
    return SupabaseSingleton.instance;
  }

  private initializeClient(): void {
    if (this.client) {
      return; // Cliente já inicializado
    }

    const config = getSupabaseConfig();
    const supabaseUrl = config.url;
    const supabaseAnonKey = config.anonKey;

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Supabase URL and Anon Key must be provided');
    }

    // Function to handle retrying fetch requests
    const fetchWithRetry = async (url: string | URL | Request, options?: RequestInit, retries = 3, backoff = 300) => {
      try {
        return await fetch(url, options);
      } catch (error) {
        if (retries <= 1) {
          console.error('Supabase fetch error (final attempt):', error);
          throw error;
        }
        
        console.warn(`Supabase fetch error, retrying (${retries-1} attempts left):`, error);
        
        // Wait for backoff duration before retrying
        await new Promise(resolve => setTimeout(resolve, backoff));
        
        // Retry with exponential backoff
        return fetchWithRetry(url, options, retries - 1, backoff * 2);
      }
    };

    this.client = createClient<Database>(
      supabaseUrl, 
      supabaseAnonKey,
      {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          flowType: 'pkce',
          detectSessionInUrl: true,
          storageKey: 'supabase-auth-token',
          storage: {
            getItem: (key: string) => {
              const item = localStorage.getItem(key);
              return item;
            },
            setItem: (key: string, value: string) => {
              localStorage.setItem(key, value);
            },
            removeItem: (key: string) => {
              localStorage.removeItem(key);
            }
          },
          debug: false, // Disable debug to reduce noise
        },
        global: {
          fetch: (url: string | URL | Request, options?: RequestInit) => {
            return fetchWithRetry(url, options)
              .catch(error => {
                console.error('❌ Supabase fetch error:', error);
                // Create a more user-friendly error
                const friendlyError = new Error(`Failed to connect to Supabase (${supabaseUrl}). Please check your network connection.`);
                // Use a type assertion for the error object to add the cause property
                (friendlyError as any).cause = error;
                throw friendlyError;
              });
          }
        }
      }
    );

    if (!import.meta.env.PROD) {
      console.log(`✅ Supabase singleton client initialized (instance: ${this.instanceId})`);
      console.log(`🌐 Environment: ${import.meta.env.VITE_APP_ENV || 'production'}`);
      console.log(`🔗 Supabase URL: ${supabaseUrl}`);
    }
  }

  public getClient(): SupabaseClient<Database> {
    if (!this.client) {
      this.initializeClient();
    }
    return this.client!;
  }

  public getInstanceInfo() {
    return {
      instanceId: this.instanceId,
      createdAt: this.createdAt,
      hasClient: !!this.client
    };
  }
}

// Export the singleton client
export const supabase = SupabaseSingleton.getInstance().getClient();

// Export instance info for debugging
export const getSupabaseInstanceInfo = () => SupabaseSingleton.getInstance().getInstanceInfo();
